/**
 * Bridge Meta Pixel identifiers (`_fbp`, `_fbc`) into first-party cookies so
 * the server-side checkout actions can forward them to api-payment / api-tracking
 * for CAPI attribution.
 *
 * The vnext storefront has no browser-side Meta Pixel JS, so without this
 * bridging neither `_fbp` nor `_fbc` would ever exist for shops that don't
 * have an upstream site appending the cookies via URL params.
 *
 * Behavior matches the legacy `apps/web/src/components/tracking.tsx` bridging
 * block, with one addition: if there is neither an upstream `_fbp` URL param
 * nor an existing `_fbp` cookie, mint one ourselves so shops without a browser
 * Meta Pixel still give Meta a browser ID to attribute against. Format matches
 * the Meta Pixel SDK: `fb.<subdomain_index=1>.<creation_ms>.<10-digit_random>`.
 *
 * Consent scope: the **mint** branch is the only net-new write this module
 * introduces, and it's gated on the visitor having granted advertising consent
 * via CookieFirst — `_fbp` is a marketing cookie and we cannot create one
 * without consent. The URL-bridge / fbclid->_fbc branches reproduce
 * long-standing legacy behavior (in production for two years) and follow the
 * legacy posture of writing pre-consent. If those need consent gating too,
 * that's a wider change that should also touch legacy.
 */

const META_COOKIE_ATTRS = 'max-age=7776000; path=/; Secure; SameSite=Lax';

function hasCookie(name: string): boolean {
  return new RegExp(`(?:^|;\\s*)${name}=`).test(document.cookie);
}

/**
 * Returns true if it's safe to write a net-new Meta marketing cookie on this
 * page.
 *
 * Decision tree:
 *  - CookieFirst not present on page → consent isn't being managed here
 *    (dev / preview / non-EU shops); allow writes.
 *  - CookieFirst present, advertising consent granted (via the
 *    `window.CookieFirst.consent` API or the `cookiefirst-consent` cookie
 *    JSON) → allow writes.
 *  - Otherwise → deny.
 *
 * We prefer the API but fall back to the cookie because `window.CookieFirst`
 * loads from a CDN and may not be ready yet on the first React mount. The
 * `cookiefirst-consent` cookie is set synchronously when the user accepts and
 * is sent on every request thereafter, so a returning visitor's consent is
 * visible immediately.
 */
function hasAdvertisingConsent(): boolean {
  const consentManaged = Boolean(
    document.querySelector('script[src*="cookiefirst.com"]')
  );
  if (!consentManaged) return true;

  const cf = (window as unknown as { CookieFirst?: { consent?: { advertising?: boolean } } }).CookieFirst;
  if (cf?.consent && typeof cf.consent.advertising === 'boolean') {
    return cf.consent.advertising;
  }

  const match = document.cookie.match(
    /(?:^|;\s*)cookiefirst-consent=([^;]*)/
  );
  if (!match) return false;
  try {
    const decoded = JSON.parse(decodeURIComponent(match[1] ?? ''));
    return decoded?.advertising === true;
  } catch {
    return false;
  }
}

export function bridgeMetaPixelCookies(): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const fbpParam = params.get('_fbp');
  const fbcParam = params.get('_fbc');
  const fbclid = params.get('fbclid');

  // _fbc: prefer explicit URL bridge, fall back to building one from fbclid
  // (the URL parameter Meta auto-appends to every ad-click destination).
  // When fbclid is present we always rewrite — that's the Meta SDK contract:
  // the timestamp in _fbc is the click time, so a fresh ad click should
  // overwrite an older _fbc so attribution windows count from the new click.
  // Matches `apps/web/src/components/tracking.tsx` legacy behavior, and like
  // legacy this branch runs pre-consent (forwarding an identifier the
  // upstream brought in, not creating one).
  if (fbcParam) {
    document.cookie = `_fbc=${fbcParam}; ${META_COOKIE_ATTRS}`;
  } else if (fbclid) {
    document.cookie = `_fbc=fb.1.${Date.now()}.${fbclid}; ${META_COOKIE_ATTRS}`;
  }

  // _fbp via URL bridge: pre-consent (matches legacy — upstream-forwarded
  // identifier, not a net-new cookie we're creating).
  if (fbpParam) {
    document.cookie = `_fbp=${fbpParam}; ${META_COOKIE_ATTRS}`;
    return;
  }

  // _fbp mint: net-new cookie creation, gated on advertising consent.
  if (!hasCookie('_fbp') && hasAdvertisingConsent()) {
    const rand = Math.floor(1_000_000_000 + Math.random() * 9_000_000_000);
    document.cookie = `_fbp=fb.1.${Date.now()}.${rand}; ${META_COOKIE_ATTRS}`;
  }
}
